import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { Checkbox, Icon } from "semantic-ui-react";
import {
    DateTimeUtils,
    DYLAvatar,
    OnHoverOnClick,
    Person,
    Table,
} from "dyl-components";

import { BulkActionsContext } from "shared/context/BulkActionsProvider";
import HoverableIconWithList from "shared/HoverableIconWithList";
import Contact from "shared/Contact";
import { StringUtils } from "utils";
import QuoteID from "shared/QuoteID";
import { QuoteBuilderContext } from "shared/context/QuoteBuilderProvider";
import {
    KEY_ACCOUNT_CO_OWNER,
    KEY_ACCOUNT_OWNER,
    KEY_CREATED,
    KEY_CREATED_BY,
    KEY_DUE_ON,
    KEY_INVOICE_STATUS,
    KEY_NUMBER_PRODUCTS,
    KEY_PAYMENT_TYPE,
    KEY_PRODUCTS,
    KEY_SUBTOTAL,
    KEY_BILLED,
    KEY_TERM,
} from "shared/constants/INVOICE_REPORT_COLUMNS";
import HoverContent from "shared/hoverContent";
import { useSelector } from "react-redux";

const InvoicesRow = ({ entry, isColumnHidden }) => {
    const { account } = entry;
    const [selectedEntries, setSelectedEntries] =
        useContext(BulkActionsContext);

    const savedSettingsRoute = useSelector((state) => state.app.savedSettingsRoute)
        
    const isSelected = (entry_id) => {
        return selectedEntries.includes(entry_id);
    };

    const onToggleContact = (entry_id) => {
        const newSelectedContacts = isSelected(entry_id)
            ? selectedEntries.filter((id) => entry_id !== id)
            : [...selectedEntries, entry_id];
        setSelectedEntries(newSelectedContacts);
    };

    const key = entry.invoice_id;

    const formatStatus = (status) => {
        if (!status) {
            return "";
        }
        const statusSplit = status.split("/");
        if (statusSplit[0] !== "rejected") {
            return StringUtils.capitalize(statusSplit[0]);
        }
        const rejectReason = statusSplit[1].split("-");
        return `${StringUtils.capitalize(statusSplit[0])}/${
            rejectReason[0] === "price"
                ? "Priced"
                : StringUtils.capitalize(rejectReason[0])
        } ${rejectReason.slice(1, rejectReason.length).join(" ")}`;
    };

    const { onViewInvoice } = useContext(QuoteBuilderContext);

    const payment_type = (() => {
        if (entry.payment_type === "e_check") {
            return "E-Check";
        }
        return entry.payment_type?.split("_").map(part =>StringUtils.capitalize(part)).join(" ") || "";
    })();
    const shouldDisplayTerm = entry.due_on && entry.due_on > 0;
    return (
        <Table.Row>
            <Table.Cell>
                <Checkbox
                    checked={isSelected(key)}
                    onChange={() => {
                        onToggleContact(key);
                    }}
                />
            </Table.Cell>
            {!isColumnHidden(KEY_CREATED) && (
                <Table.Cell>
                    <div>
                        <b>
                            {DateTimeUtils.formatEpoch(
                                entry.created,
                                DateTimeUtils.WORD_DATE_FORMAT
                            )}
                        </b>
                    </div>
                    <div>
                        {DateTimeUtils.formatEpoch(
                            entry.created,
                            DateTimeUtils.TIME_FORMAT
                        )}
                    </div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_DUE_ON) && (
                <Table.Cell>
                    <div>
                        <b>
                            {shouldDisplayTerm &&
                                DateTimeUtils.formatEpoch(
                                    entry.due_on,
                                    DateTimeUtils.WORD_DATE_FORMAT
                                )}
                        </b>
                    </div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_TERM) && (
                <Table.Cell>{entry.term ? `Net/${entry.term}` : shouldDisplayTerm &&  "Default"}</Table.Cell>
            )}
            {!isColumnHidden(KEY_BILLED) && (
                <Table.Cell>{StringUtils.capitalize(entry.billed)}</Table.Cell>
            )}
            <Table.Cell >
                <div>
                    <QuoteID
                        onEditQuote={() => {
                            onViewInvoice(entry.invoice_id);
                        }}
                        id={entry.invoice_id}
                    />
                </div>
            </Table.Cell>
            <Table.Cell>
                {Boolean(account?.id) && (
                    <Contact
                        contact={{
                            ...(account || {}),
                            email: account?.email || {},
                            phone: account?.phone || {},
                            account_id: account.id,
                            archived: account?.archived
                        }}
                        name={account.name}
                        savedSettingsRoute={savedSettingsRoute}
                        account
                        linkToContact={
                            <Link to={`/account/${account.id}`} target="_blank">
                                {account?.name}
                            </Link>
                        }
                    />
                )}
            </Table.Cell>
            {!isColumnHidden(KEY_INVOICE_STATUS) && (
                <Table.Cell>{formatStatus(entry.invoice_status)}</Table.Cell>
            )}
            {!isColumnHidden(KEY_ACCOUNT_OWNER) && (
                <Table.Cell>
                    {entry?.account?.owner?.id ? <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={entry?.account?.owner}
                            />
                        }
                        clickable={false}
                        trigger={entry?.account?.owner ?
                            <div className='TableTruncateFixedWidth'>
                                <Person
                                    username={entry?.account?.owner.username}
                                    email={entry?.account?.owner.email}
                                    isInactiveUser={!entry?.account?.owner?.active}
                                    popup={false}
                                    title={" "}
                                />
                            </div>
                            : undefined}
                    /> : "Unassigned"}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_ACCOUNT_CO_OWNER) && (
                <Table.Cell
                >
                    {entry?.account?.co_owner?.id ? <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={entry?.account?.co_owner}
                            />
                        }
                        clickable={false}
                        trigger={entry?.account?.co_owner ?
                            <div className='TableTruncateFixedWidth'>
                                <Person
                                    username={entry?.account?.co_owner.username}
                                    email={entry?.account?.co_owner.email}
                                    isInactiveUser={!entry?.account?.co_owner?.active}
                                    popup={false}
                                    title={" "}
                                />
                            </div>
                            : undefined}
                    /> : "Unassigned"}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CREATED_BY) && (
                <Table.Cell>
                    {entry.created_by?.id ? (
                        <OnHoverOnClick
                            hoverContent={
                                <HoverContent
                                    user={entry.created_by}
                                />
                            }
                            clickContent={null}
                            trigger={
                                entry.created_by ? (
                                    <DYLAvatar
                                        name={entry.created_by.username}
                                        disabled
                                        size="2.5em"
                                        inactive={!entry.created_by.active}
                                        title={" "}
                                    />
                                ) : undefined
                            }
                        />
                    ) : (
                        <Icon
                            className='fas fa-robot AutomationIcon'
                            circular
                            size='large' 
                        />
                    )}
                </Table.Cell>
            )}            
            {!isColumnHidden(KEY_PRODUCTS) && (
                <Table.Cell>
                    <HoverableIconWithList
                        icon={
                            <Icon className="fa-solid fa-box" color="primary" />
                        }
                        iconContent={"Products"}
                        items={entry?.products}
                    />
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_NUMBER_PRODUCTS) && (
                <Table.Cell>{entry.number_products}</Table.Cell>
            )}
            {!isColumnHidden(KEY_SUBTOTAL) && (
                <Table.Cell>
                    {entry.subtotal &&
                        `$${StringUtils.formatDecimal(
                            String(entry.subtotal),
                            true
                        )}`}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_PAYMENT_TYPE) && (
                <Table.Cell
                    className="InvoicesTable__MediumFixedWidth"
                    width={2}
                >
                    {payment_type}
                </Table.Cell>
            )}
        </Table.Row>
    );
};

export default InvoicesRow;
